import { RotateCcw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ROOM_KIND_LABEL } from '@obraplan/core/domain/takeoff/room';
import { steelGaugeLabel } from '@obraplan/core/domain/takeoff/steel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  quantosProprios,
  textoDoFator,
  type LinhaDeFator,
  type MediaAoLado,
  type ServicoDoCard,
} from './fatores-proprios';
import { metros, paraNumero, paraTexto } from './formato';
import type { MudancasDoTipo } from './media-no-tipo';

/**
 * O fator próprio no card da estaca e do cômodo (Fase 39, etapa 3 — D14, RN-511 a RN-513).
 *
 * Cada item dos serviços que valem ali, com o fator que vale sem o próprio ao lado — o do tipo, ou
 * o índice da obra —, o campo do próprio e a volta. O próprio aparece marcado "próprio": um 1,10
 * no lugar do 1,05 do tipo, sem a marca, é um número que alguém desfaz achando que é erro. Não
 * pede motivo (D9).
 *
 * Fechado por padrão, com o número de próprios no título: o card da peça é para nome, tipo e
 * lugar, e a lista de itens é longa.
 */
/**
 * Levar o próprio de uma linha para a composição do tipo (Fase 41, RN-536): o número sai do card
 * e vai para o tipo, que todo bloco dele lê. Só o bloco tem — a estaca muda o tipo no cadastro.
 */
export interface AplicarAoTipo {
  /** "BL2E" — o link diz para onde o número vai. */
  readonly nome: string;
  readonly onAplicar: (serviceKey: string, itemKey: string, factor: number) => void;
}

export function FatoresProprios({
  idBase,
  titulo,
  servicos,
  rotuloDaVolta,
  vazio,
  onMudar,
  aplicarAoTipo,
}: {
  /** O prefixo dos ids dos campos — o da peça ou do cômodo. */
  idBase: string;
  /** "Fatores desta estaca". */
  titulo: string;
  servicos: readonly ServicoDoCard[];
  /** "Voltar ao do tipo", "Voltar ao índice". */
  rotuloDaVolta: string;
  /** O que dizer quando não há item nenhum a mostrar. */
  vazio: string;
  /** `factor` ausente é a volta. */
  onMudar: (serviceKey: string, itemKey: string, factor: number | undefined) => void;
  /** O link "aplicar ao tipo" ao lado do próprio (Fase 41). Ausente, a linha é a de sempre. */
  aplicarAoTipo?: AplicarAoTipo | undefined;
}) {
  const proprios = quantosProprios(servicos);
  const comMedia = servicos.some((servico) =>
    servico.linhas.some((linha) => linha.mediaAoLado !== undefined),
  );

  return (
    <details className="flex flex-col gap-2 text-xs">
      <summary className="cursor-pointer text-sm font-medium">
        {titulo}
        {proprios > 0 && (
          <span className="ml-1 font-normal text-(--color-content-muted)">
            ({String(proprios)} {proprios === 1 ? 'próprio' : 'próprios'})
          </span>
        )}
      </summary>
      {servicos.length === 0 ? (
        <p className="mt-2 text-(--color-content-muted)">{vazio}</p>
      ) : (
        <div className="mt-2 flex flex-col gap-3">
          <p className="text-(--color-content-muted)">
            A medida é a da planta e não muda; o fator transforma a medida em material ou mão de
            obra. O próprio vale só aqui, por cima do outro, e não pede motivo.
            {comMedia &&
              ' A média ao lado é a das obras marcadas: só para ler, ela não muda o fator.'}
          </p>
          {servicos.map((servico) => (
            <section key={servico.serviceKey} aria-label={servico.servico}>
              <h4 className="mb-1 font-semibold">{servico.servico}</h4>
              <ul className="flex flex-col gap-2">
                {servico.linhas.map((linha) => (
                  <LinhaDoFator
                    key={linha.itemKey}
                    id={`${idBase}-${servico.serviceKey}-${linha.itemKey}`}
                    linha={linha}
                    rotuloDaVolta={rotuloDaVolta}
                    onMudar={(fator) => onMudar(linha.serviceKey, linha.itemKey, fator)}
                    {...(aplicarAoTipo === undefined ? {} : { aplicarAoTipo })}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </details>
  );
}

/**
 * "1,05 do tipo", "1 da obra" — e "1,0363 do tipo, da média" na linha do tipo que nasceu da média
 * ou que o "Atualizar" trouxe (Fase 42, etapa 4 — ADR-191): quem lê sabe que ela muda com a média.
 * A da média vai com as quatro casas da média em toda parte; a digitada, com as casas que tiver.
 */
function baseEmTexto(linha: LinhaDeFator): string {
  if (linha.base === undefined) return 'sem fator — não calcula';
  if (linha.origemDaBase === 'TIPO' && linha.baseDaMedia === true) {
    return `${metros(linha.base, 4)} do tipo, da média`;
  }
  const numero = textoDoFator(linha.base);
  return linha.origemDaBase === 'TIPO' ? `${numero} do tipo` : `${numero} da obra`;
}

/**
 * "média 1,0363 · 3 obras" — a média da fundação ao lado do fator (Fase 41, RN-554, F6), com as
 * quatro casas da aba dos itens. Só para ler: o tipo e o próprio ficam por cima dela (D8).
 */
function mediaEmTexto(media: MediaAoLado): string {
  return `média ${metros(media.rate, 4)} · ${String(media.projects)} ${media.projects === 1 ? 'obra' : 'obras'}`;
}

/** A média igual ao número da linha (nas quatro casas) não se repete, como na aba dos itens. */
function MediaDaLinha({
  media,
  valor,
}: {
  media: MediaAoLado | undefined;
  valor: number | undefined;
}) {
  if (media === undefined) return null;
  if (valor !== undefined && metros(valor, 4) === metros(media.rate, 4)) return null;
  return <span className="tabular text-(--color-content-muted)">{mediaEmTexto(media)}</span>;
}

function LinhaDoFator({
  id,
  linha,
  rotuloDaVolta,
  onMudar,
  aplicarAoTipo,
}: {
  id: string;
  linha: LinhaDeFator;
  rotuloDaVolta: string;
  onMudar: (factor: number | undefined) => void;
  aplicarAoTipo?: AplicarAoTipo;
}) {
  const valorProprio = linha.proprio;
  const proprio = valorProprio !== undefined;

  return (
    <li className="flex flex-col gap-1 rounded-md border border-(--color-border-subtle) p-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <label htmlFor={id} className="font-medium break-words">
          {linha.item}
        </label>
        <span className="text-(--color-content-muted)">{linha.unidade}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <FatorInput
          id={id}
          valor={linha.proprio}
          {...(linha.base === undefined ? {} : { sugestao: linha.base })}
          onAplicar={(fator) =>
            // Igual ao que já vale não é fator próprio: volta a valer o outro, como o comprimento
            // igual ao do tipo deixa de ser correção (RN-362).
            onMudar(linha.base !== undefined && fator === linha.base ? undefined : fator)
          }
        />
        {proprio ? (
          <>
            {/* O que veio da média do tipo de cômodo diz de qual tipo (Fase 40, RN-527). */}
            <Badge tone="info">
              {linha.daMediaDoTipo === undefined
                ? 'próprio'
                : `média ${ROOM_KIND_LABEL[linha.daMediaDoTipo.roomKind].toLowerCase()}`}
            </Badge>
            <span className="text-(--color-content-muted)">
              {linha.base === undefined ? 'sem fator ao lado' : `no lugar de ${baseEmTexto(linha)}`}
            </span>
            <MediaDaLinha media={linha.mediaAoLado} valor={valorProprio} />
            <Button variant="ghost" size="sm" onClick={() => onMudar(undefined)}>
              <RotateCcw className="size-4" /> {rotuloDaVolta}
            </Button>
            {aplicarAoTipo !== undefined && valorProprio !== undefined && (
              <Button
                variant="link"
                size="sm"
                className="h-auto px-0 text-xs md:h-auto"
                onClick={() =>
                  aplicarAoTipo.onAplicar(linha.serviceKey, linha.itemKey, valorProprio)
                }
              >
                Aplicar ao tipo {aplicarAoTipo.nome}
              </Button>
            )}
          </>
        ) : (
          <>
            <span className="text-(--color-content-muted)">{baseEmTexto(linha)}</span>
            <MediaDaLinha media={linha.mediaAoLado} valor={linha.base} />
          </>
        )}
      </div>
    </li>
  );
}

/** "27/10", de `aaaa-mm-dd` — o dia da foto, como nas faixas da aba dos itens. */
function diaEMes(dia: string): string {
  const [, mes, d] = dia.split('-');
  return `${d ?? ''}/${mes ?? ''}`;
}

/**
 * "A média mudou" no tipo (Fase 42, etapa 4 — RN-583): as linhas da composição e as bitolas da
 * taxa do tipo que seguem a média e que a de hoje mudou, com o "Atualizar pela média". É a faixa
 * da aba dos itens (Fases 40 e 41), no card da peça, logo abaixo do campo "Tipo": sem tela nova.
 * O "Atualizar" troca só o que segue a média; o número digitado no tipo fica, com a média nova ao
 * lado (D8). Sem mudança, nada.
 */
export function AMediaDoTipoMudou({
  tipo,
  paraQuem,
  mudancas,
  onAtualizar,
}: {
  /** "P20". */
  tipo: string;
  /** "todo pilar dele", "toda viga dele" — quem o "Atualizar" alcança. */
  paraQuem: string;
  mudancas: MudancasDoTipo;
  onAtualizar: () => void;
}) {
  const { fatores, taxas } = mudancas;
  if (fatores.length + taxas.length === 0) return null;
  const desde = fatores[0]?.desde ?? taxas[0]?.desde;
  return (
    <section
      aria-label={`A média do tipo ${tipo} mudou`}
      className="flex flex-col gap-2 rounded-md border border-(--color-state-warn) bg-(--color-surface-muted) px-3 py-2 text-xs"
    >
      <p className="font-medium">
        A média mudou{desde === undefined ? '' : ` desde ${diaEMes(desde)}`}
      </p>
      <ul className="flex flex-col gap-0.5">
        {fatores.map((mudanca) => (
          <li key={`${mudanca.serviceKey}|${mudanca.itemKey}`} className="tabular">
            {mudanca.servico} · {mudanca.descricao}: {metros(mudanca.de, 4)} →{' '}
            {metros(mudanca.para, 4)}
          </li>
        ))}
        {taxas.map((mudanca) => (
          <li key={mudanca.gauge} className="tabular">
            {steelGaugeLabel(mudanca.gauge)}: {metros(mudanca.de, 3)} → {metros(mudanca.para, 3)}{' '}
            kg/m³
          </li>
        ))}
      </ul>
      <p className="text-(--color-content-muted)">
        O {tipo} segue a média nessas linhas. Atualizar troca só o que segue a média, para{' '}
        {paraQuem}; o número digitado no tipo fica, com a média nova ao lado.
      </p>
      <div>
        <Button size="sm" variant="secondary" onClick={onAtualizar}>
          Atualizar pela média
        </Button>
      </div>
    </section>
  );
}

/**
 * O campo do fator: aplica ao sair ou no Enter, e não a cada tecla — o "0," do caminho até 0,5
 * seria um zero recusado no meio da digitação. Vazio volta ao que estava.
 */
function FatorInput({
  id,
  valor,
  sugestao,
  onAplicar,
}: {
  id: string;
  valor: number | undefined;
  sugestao?: number;
  onAplicar: (fator: number) => void;
}) {
  const [texto, setTexto] = useState(valor === undefined ? '' : paraTexto(valor));
  const [erro, setErro] = useState<string | undefined>(undefined);

  useEffect(() => {
    setTexto(valor === undefined ? '' : paraTexto(valor));
  }, [valor]);

  const aplicar = () => {
    const numero = paraNumero(texto);
    if (numero === undefined) {
      setTexto(valor === undefined ? '' : paraTexto(valor));
      setErro(undefined);
      return;
    }
    if (numero <= 0) {
      setErro('O fator deve ser maior que zero.');
      return;
    }
    setErro(undefined);
    if (numero !== valor) onAplicar(numero);
  };

  return (
    <span className="flex flex-col gap-0.5">
      <Input
        id={id}
        inputMode="decimal"
        value={texto}
        placeholder={sugestao === undefined ? 'fator' : textoDoFator(sugestao)}
        aria-invalid={erro !== undefined}
        className="w-28"
        onChange={(evento) => setTexto(evento.target.value)}
        onBlur={aplicar}
        onKeyDown={(evento) => {
          if (evento.key === 'Enter') aplicar();
        }}
      />
      {erro !== undefined && (
        <span role="alert" className="text-(--color-state-danger)">
          {erro}
        </span>
      )}
    </span>
  );
}
